import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box, Button, Chip, CircularProgress, Container, Divider,
  Drawer, FormControlLabel, Grid, IconButton, InputAdornment,
  LinearProgress, Paper, Stack, Switch, TextField, ToggleButton,
  ToggleButtonGroup, Tooltip, Typography,
} from "@mui/material";
import {
  MdAdd, MdClose, MdDragIndicator, MdEdit, MdExpandLess, MdExpandMore,
  MdGridView, MdImage, MdLabelOutline, MdList, MdSave, MdSearch,
  MdViewCarousel, MdWarning, MdMovie,
} from "react-icons/md";
import { db, storage } from "../../firebase/firebase-config";
import Colors from "../../assets/styles/Colors";
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy,
  useSortable, arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const CATEGORY_LABELS = {
  mastermind: "NUMIND", trading: "NUINCOME",
  nuwealth: "NUWEALTH", nubusiness: "NUBUSINESS",
};
const CATEGORY_COLORS = {
  mastermind: { bg: "#3b1a6b", color: "#c084fc" },
  trading:    { bg: "#0a2e1f", color: "#34d399" },
  nuwealth:   { bg: "#0a2618", color: "#4ade80" },
  nubusiness: { bg: "#2a1200", color: "#fb923c" },
};

// ── Data helpers ───────────────────────────────────────────────────────────────

async function fetchSections() {
  const snap = await db.collection("sections").get();
  return snap.docs
    .filter(d => d.id !== "_hero")
    .map(d => ({ id: d.id, ...d.data() }))
    .sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
}
async function fetchHero() {
  // Primary location — inside sections (same rules as section cards, readable by members)
  const snap = await db.collection("sections").doc("_hero").get();
  if (snap.exists) return snap.data();

  // Fallback: migrate legacy location → sections/_hero so the landing can read it
  const legacy = await db.collection("ecosystem_config").doc("hero").get();
  if (legacy.exists) {
    const data = legacy.data();
    await db.collection("sections").doc("_hero").set(data);
    return data;
  }

  return { type: "none", slides: [], videoUrl: "", autoplay: true, loop: true };
}
async function fetchContentForSection(tag) {
  if (!tag) return [];
  const snap = await db.collection("academy_content")
    .where("tags", "array-contains", tag)
    .orderBy("orderNumber", "asc").get();
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

// ── Hero panel ─────────────────────────────────────────────────────────────────

function HeroPanel({ hero, onChange }) {
  const fileRef  = useRef(null);
  const [type,     setType]     = useState(hero.type     || "none");
  const [slides,   setSlides]   = useState(hero.slides   || []);
  const [videoUrl, setVideoUrl] = useState(hero.videoUrl || "");
  const [autoplay, setAutoplay] = useState(hero.autoplay !== false);
  const [loop,     setLoop]     = useState(hero.loop     !== false);
  const [uploading, setUploading] = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [saved,    setSaved]    = useState(false);

  async function addSlide(file) {
    setUploading(true);
    try {
      const id  = `${Date.now()}`;
      const ref = storage.ref(`ecosystem_hero/${id}_${file.name}`);
      await ref.put(file);
      const url = await ref.getDownloadURL();
      setSlides(prev => [...prev, { id, imageUrl: url, link: "" }]);
    } catch (e) { alert("Error subiendo imagen: " + e.message); }
    finally { setUploading(false); }
  }

  function updateLink(id, link) { setSlides(prev => prev.map(s => s.id === id ? { ...s, link } : s)); }
  function removeSlide(id) { setSlides(prev => prev.filter(s => s.id !== id)); }

  async function handleSave() {
    setSaving(true);
    try {
      const data = { type, slides, videoUrl: videoUrl.trim(), autoplay, loop };
      await db.collection("sections").doc("_hero").set(data);
      onChange(data);
      setSaved(true); setTimeout(() => setSaved(false), 2000);
    } catch (e) { alert("Error: " + e.message); }
    finally { setSaving(false); }
  }

  const tgSx = {
    "& .MuiToggleButton-root": { color: Colors.textSecondary, borderColor: Colors.borderSecondary, textTransform: "none", fontSize: 12, px: 1.75 },
    "& .Mui-selected": { color: `${Colors.primary} !important`, background: `${Colors.primary}22 !important`, borderColor: `${Colors.primary}66 !important` },
  };

  return (
    <Paper sx={{ mb: 3, p: 3, borderRadius: 3, border: `1px solid ${Colors.borderSecondary}`, background: Colors.bgSecondary }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2.5}>
        <Box>
          <Typography variant="h6" sx={{ color: Colors.textPrimary, fontWeight: 700 }}>Panel de Inicio — Hero</Typography>
          <Typography variant="caption" sx={{ color: Colors.textSecondary }}>
            Lo primero que ven los miembros al entrar al Ecosistema
          </Typography>
        </Box>
        <Button
          variant="contained" size="small" startIcon={saving ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <MdSave />}
          onClick={handleSave} disabled={saving}
          sx={{ textTransform: "none", borderRadius: 2, backgroundColor: saved ? Colors.success : Colors.primary, "&:hover": { backgroundColor: Colors.primaryDark } }}
        >
          {saved ? "Guardado ✓" : "Guardar hero"}
        </Button>
      </Stack>

      <ToggleButtonGroup value={type} exclusive onChange={(_, v) => v && setType(v)} size="small" sx={{ ...tgSx, mb: 2.5 }}>
        <ToggleButton value="none">Sin hero</ToggleButton>
        <ToggleButton value="carousel"><MdViewCarousel size={14} style={{ marginRight: 5 }} />Carrusel de imágenes</ToggleButton>
        <ToggleButton value="video"><MdMovie size={14} style={{ marginRight: 5 }} />Video</ToggleButton>
      </ToggleButtonGroup>

      {type === "carousel" && (
        <Box>
          <Typography variant="caption" sx={{ color: Colors.textSecondary, letterSpacing: 1, display: "block", mb: 1.5 }}>
            DIAPOSITIVAS (hasta 5) — El admin puede subir imágenes y asignarles un link opcional
          </Typography>
          <Grid container spacing={2}>
            {slides.map(slide => (
              <Grid item xs={6} sm={4} md={3} key={slide.id}>
                <Box sx={{ borderRadius: 2, overflow: "hidden", position: "relative", aspectRatio: "16/9", border: `1px solid ${Colors.borderSecondary}`, background: Colors.bgTertiary }}>
                  <img src={slide.imageUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                  <IconButton size="small" onClick={() => removeSlide(slide.id)}
                    sx={{ position: "absolute", top: 4, right: 4, background: "rgba(0,0,0,0.65)", color: "#fff", width: 24, height: 24, "&:hover": { background: "rgba(180,0,0,0.7)" } }}>
                    <MdClose size={13} />
                  </IconButton>
                </Box>
                <TextField size="small" placeholder="Link al hacer clic (opcional)" value={slide.link}
                  onChange={e => updateLink(slide.id, e.target.value)}
                  fullWidth sx={{ mt: 1, "& .MuiInputBase-input": { color: Colors.textPrimary, fontSize: 12 }, "& fieldset": { borderColor: Colors.borderSecondary } }} />
              </Grid>
            ))}
            {slides.length < 5 && (
              <Grid item xs={6} sm={4} md={3}>
                <Box
                  onClick={() => !uploading && fileRef.current?.click()}
                  sx={{
                    aspectRatio: "16/9", borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "center",
                    border: `2px dashed ${Colors.borderPrimary}`, cursor: uploading ? "wait" : "pointer",
                    background: Colors.bgTertiary, transition: "border-color 0.15s",
                    "&:hover": { borderColor: Colors.primary },
                  }}
                >
                  {uploading ? <CircularProgress size={24} /> : (
                    <Stack alignItems="center" spacing={0.5}>
                      <MdAdd size={28} color={Colors.textMuted} />
                      <Typography variant="caption" sx={{ color: Colors.textMuted }}>Subir imagen</Typography>
                    </Stack>
                  )}
                </Box>
              </Grid>
            )}
          </Grid>
          <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }}
            onChange={e => { if (e.target.files?.[0]) { addSlide(e.target.files[0]); e.target.value = ""; } }} />
          <Stack direction="row" spacing={2} mt={2} alignItems="center">
            <FormControlLabel control={<Switch size="small" checked={autoplay} onChange={e => setAutoplay(e.target.checked)} />}
              label={<Typography variant="caption" sx={{ color: Colors.textSecondary }}>Auto-avanzar</Typography>} />
          </Stack>
        </Box>
      )}

      {type === "video" && (
        <Stack spacing={2}>
          <TextField
            label="URL del video" placeholder="https://... (MP4 directo, YouTube embed, Vimeo embed)"
            value={videoUrl} onChange={e => setVideoUrl(e.target.value)}
            fullWidth size="small"
            InputProps={{ startAdornment: <InputAdornment position="start"><MdMovie color={Colors.textMuted} /></InputAdornment> }}
            sx={{ "& .MuiInputBase-input": { color: Colors.textPrimary }, "& fieldset": { borderColor: Colors.borderSecondary } }}
          />
          <Stack direction="row" spacing={3}>
            <FormControlLabel control={<Switch size="small" checked={autoplay} onChange={e => setAutoplay(e.target.checked)} />}
              label={<Typography variant="caption" sx={{ color: Colors.textSecondary }}>Reproducir automáticamente (silenciado)</Typography>} />
            <FormControlLabel control={<Switch size="small" checked={loop} onChange={e => setLoop(e.target.checked)} />}
              label={<Typography variant="caption" sx={{ color: Colors.textSecondary }}>Repetir en bucle</Typography>} />
          </Stack>
          {videoUrl && (
            <Typography variant="caption" sx={{ color: Colors.textMuted }}>
              Vista previa disponible en la app del miembro.
            </Typography>
          )}
        </Stack>
      )}
    </Paper>
  );
}

// ── Sortable section row ───────────────────────────────────────────────────────

function SortableSection({ sec, onEdit, onSaved }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: sec.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.45 : 1, zIndex: isDragging ? 50 : "auto" };

  const bannerFileRef = useRef(null);
  const [expanded,        setExpanded]        = useState(false);
  const [displayMode,     setDisplayMode]     = useState(sec.displayMode   || "count");
  const [bannerUrl,       setBannerUrl]       = useState(sec.bannerUrl     || "");
  const [bannerUploading, setBannerUploading] = useState(false);
  const [contentSize,     setContentSize]     = useState(sec.contentSize   || "md");
  const [featuredMode,    setFeaturedMode]    = useState(sec.featuredMode  || "latest");
  const [selected,        setSelected]        = useState(sec.featuredContent || []);
  const [content,         setContent]         = useState(null);
  const [loadingC,        setLoadingC]        = useState(false);
  const [search,          setSearch]          = useState("");
  const [saving,          setSaving]          = useState(false);
  const [saved,           setSaved]           = useState(false);

  const cat = CATEGORY_COLORS[sec.category] || { bg: Colors.bgTertiary, color: Colors.textSecondary };

  async function handleExpand() {
    setExpanded(v => !v);
    if (!content && !loadingC) {
      setLoadingC(true);
      try { setContent(await fetchContentForSection(sec.firestoreTag || sec.id)); }
      catch { setContent([]); }
      finally { setLoadingC(false); }
    }
  }

  async function handleSave() {
    setSaving(true);
    try {
      const update = { displayMode, bannerUrl: bannerUrl.trim(), contentSize, featuredMode, featuredContent: selected };
      await db.collection("sections").doc(sec.id).update(update);
      onSaved?.({ ...sec, ...update });
      setSaved(true); setTimeout(() => setSaved(false), 2000);
    } catch (e) { alert("Error al guardar: " + e.message); }
    finally { setSaving(false); }
  }

  function toggleItem(id) { setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]); }

  const filteredContent = (content || []).filter(c => !search.trim() || (c.name || "").toLowerCase().includes(search.toLowerCase()));

  const tgSx = {
    "& .MuiToggleButton-root": { color: Colors.textSecondary, borderColor: Colors.borderSecondary, textTransform: "none", fontSize: 12, px: 1.5, gap: 0.5 },
    "& .Mui-selected": { color: `${Colors.primary} !important`, background: `${Colors.primary}22 !important`, borderColor: `${Colors.primary}66 !important` },
  };

  return (
    <Paper ref={setNodeRef} style={style} elevation={0}
      sx={{ border: `1px solid ${isDragging ? Colors.primary + "88" : Colors.borderSecondary}`, borderRadius: 2, mb: 1.5, overflow: "hidden", background: Colors.bgSecondary }}>

      {/* Row header */}
      <Stack direction="row" alignItems="center" sx={{ px: 2, py: 1.5, gap: 1.5 }}>
        <Box {...attributes} {...listeners} sx={{ cursor: "grab", color: Colors.textMuted, display: "flex", alignItems: "center", flexShrink: 0, "&:active": { cursor: "grabbing" } }}>
          <MdDragIndicator size={20} />
        </Box>

        {/* Thumb */}
        <Box sx={{ width: 52, height: 34, borderRadius: 1, overflow: "hidden", flexShrink: 0, background: sec.gradient || Colors.bgTertiary, border: `1px solid ${Colors.borderSecondary}` }}>
          {sec.imageURL && <img src={sec.imageURL} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />}
        </Box>

        <Chip label={CATEGORY_LABELS[sec.category] || sec.category?.toUpperCase() || "—"}
          size="small" sx={{ background: cat.bg, color: cat.color, fontSize: 9, letterSpacing: 1.5, fontWeight: 700, height: 22, flexShrink: 0 }} />

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ color: Colors.textPrimary, fontWeight: 600, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {sec.icon && <span style={{ marginRight: 6 }}>{sec.icon}</span>}{sec.displayName}
          </Typography>
          <Typography sx={{ color: Colors.textMuted, fontSize: 11, fontFamily: "monospace" }}>{sec.firestoreTag}</Typography>
        </Box>

        <Chip label={displayMode === "count" ? "Conteo" : displayMode === "carousel" ? "Carrusel" : "Tira"}
          size="small" sx={{ background: Colors.bgTertiary, color: Colors.textMuted, fontSize: 10, height: 22, flexShrink: 0 }} />

        <Tooltip title="Editar sección (nombre, imagen, tag...)">
          <IconButton size="small" onClick={onEdit} sx={{ color: Colors.textSecondary, "&:hover": { color: Colors.textPrimary } }}>
            <MdEdit size={16} />
          </IconButton>
        </Tooltip>
        <Tooltip title={expanded ? "Colapsar" : "Configurar visualización"}>
          <IconButton size="small" onClick={handleExpand} sx={{ color: Colors.textSecondary }}>
            {expanded ? <MdExpandLess /> : <MdExpandMore />}
          </IconButton>
        </Tooltip>
      </Stack>

      {/* Expanded settings */}
      {expanded && (
        <>
          <Divider sx={{ borderColor: Colors.borderSecondary }} />
          <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 2.5 }}>

            <Stack spacing={1}>
              <Typography variant="caption" sx={{ color: Colors.textSecondary, letterSpacing: 1 }}>MODO DE VISUALIZACIÓN (app)</Typography>
              <ToggleButtonGroup value={displayMode} exclusive onChange={(_, v) => v && setDisplayMode(v)} size="small" sx={tgSx}>
                <ToggleButton value="count"><MdGridView size={14} />Solo conteo</ToggleButton>
                <ToggleButton value="carousel"><MdViewCarousel size={14} />Carrusel</ToggleButton>
                <ToggleButton value="strip"><MdList size={14} />Tira</ToggleButton>
              </ToggleButtonGroup>
            </Stack>

            <Stack spacing={1}>
              <Typography variant="caption" sx={{ color: Colors.textSecondary, letterSpacing: 1 }}>IMAGEN DE BANNER</Typography>
              <Box
                onClick={() => !bannerUploading && bannerFileRef.current?.click()}
                sx={{
                  height: 110, borderRadius: 2, overflow: "hidden", position: "relative", cursor: bannerUploading ? "wait" : "pointer",
                  background: bannerUrl ? "none" : Colors.bgTertiary,
                  border: `2px dashed ${bannerUrl ? "transparent" : Colors.borderPrimary}`,
                  transition: "border-color 0.15s", "&:hover": { borderColor: bannerUrl ? "transparent" : Colors.primary },
                }}
              >
                {bannerUrl ? (
                  <>
                    <img src={bannerUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                    <Box sx={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", opacity: 0, transition: "opacity 0.2s", "&:hover": { opacity: 1 } }}>
                      <Typography variant="caption" sx={{ color: "#fff", fontWeight: 600 }}>Cambiar imagen</Typography>
                    </Box>
                    <IconButton size="small" onClick={e => { e.stopPropagation(); setBannerUrl(""); }}
                      sx={{ position: "absolute", top: 5, right: 5, background: "rgba(0,0,0,0.65)", color: "#fff", width: 22, height: 22, "&:hover": { background: "rgba(180,0,0,0.7)" } }}>
                      <MdClose size={13} />
                    </IconButton>
                  </>
                ) : bannerUploading ? (
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  <Stack alignItems="center" justifyContent="center" sx={{ height: "100%", gap: 0.5 }}>
                    <MdImage size={28} color={Colors.textMuted} />
                    <Typography variant="caption" sx={{ color: Colors.textMuted }}>Subir imagen de banner</Typography>
                  </Stack>
                )}
              </Box>
              <input ref={bannerFileRef} type="file" accept="image/*" style={{ display: "none" }}
                onChange={async e => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  e.target.value = "";
                  setBannerUploading(true);
                  try {
                    const ref = storage.ref(`sections/${sec.id}/banner_${Date.now()}_${file.name}`);
                    await ref.put(file);
                    setBannerUrl(await ref.getDownloadURL());
                  } catch (err) { alert("Error subiendo imagen: " + err.message); }
                  finally { setBannerUploading(false); }
                }}
              />
            </Stack>

            {displayMode !== "count" && (
              <Stack spacing={1}>
                <Typography variant="caption" sx={{ color: Colors.textSecondary, letterSpacing: 1 }}>TAMAÑO DE TARJETAS</Typography>
                <ToggleButtonGroup value={contentSize} exclusive onChange={(_, v) => v && setContentSize(v)} size="small" sx={tgSx}>
                  <ToggleButton value="sm">Pequeño</ToggleButton>
                  <ToggleButton value="md">Mediano</ToggleButton>
                  <ToggleButton value="lg">Grande</ToggleButton>
                </ToggleButtonGroup>
              </Stack>
            )}

            <Stack spacing={1}>
              <Typography variant="caption" sx={{ color: Colors.textSecondary, letterSpacing: 1 }}>CONTENIDO DESTACADO</Typography>
              <ToggleButtonGroup value={featuredMode} exclusive onChange={(_, v) => v && setFeaturedMode(v)} size="small" sx={tgSx}>
                <ToggleButton value="latest">Más recientes</ToggleButton>
                <ToggleButton value="spread">Selección manual</ToggleButton>
              </ToggleButtonGroup>
            </Stack>

            {featuredMode === "spread" && (
              <Box>
                <Typography variant="caption" sx={{ color: Colors.textSecondary, letterSpacing: 1, display: "block", mb: 1 }}>SELECCIONAR CONTENIDO DESTACADO</Typography>
                <TextField size="small" placeholder="Buscar…" value={search} onChange={e => setSearch(e.target.value)} fullWidth
                  InputProps={{ startAdornment: <InputAdornment position="start"><MdSearch size={16} color={Colors.textMuted} /></InputAdornment>, sx: { color: Colors.textPrimary } }}
                  sx={{ mb: 1, "& fieldset": { borderColor: Colors.borderSecondary }, "& input": { color: Colors.textPrimary, fontSize: 13 } }}
                />
                {loadingC ? <CircularProgress size={20} /> : (
                  <Box sx={{ maxHeight: 200, overflowY: "auto", border: `1px solid ${Colors.borderSecondary}`, borderRadius: 1.5 }}>
                    {filteredContent.length === 0 && (
                      <Typography variant="caption" sx={{ color: Colors.textMuted, display: "block", p: 1.5 }}>Sin contenido en esta sección</Typography>
                    )}
                    {filteredContent.map(item => {
                      const on = selected.includes(item.id);
                      return (
                        <Box key={item.id} onClick={() => toggleItem(item.id)}
                          sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 0.75, cursor: "pointer", background: on ? Colors.primary + "18" : "transparent", "&:hover": { background: on ? Colors.primary + "28" : Colors.bgTertiary }, borderBottom: `1px solid ${Colors.borderSecondary}` }}>
                          <Box sx={{ width: 10, height: 10, borderRadius: "50%", border: `2px solid ${on ? Colors.primary : Colors.borderPrimary}`, background: on ? Colors.primary : "transparent", flexShrink: 0, transition: "all 0.15s" }} />
                          <Typography sx={{ fontSize: 12.5, color: on ? Colors.textPrimary : Colors.textSecondary, flex: 1 }}>{item.name}</Typography>
                        </Box>
                      );
                    })}
                  </Box>
                )}
                {selected.length > 0 && (
                  <Typography variant="caption" sx={{ color: Colors.textMuted, display: "block", mt: 0.75 }}>{selected.length} elemento(s) seleccionado(s)</Typography>
                )}
              </Box>
            )}

            <Box>
              <Button size="small" variant="contained" onClick={handleSave} disabled={saving}
                sx={{ textTransform: "none", borderRadius: 2, backgroundColor: saved ? Colors.success : Colors.primary, "&:hover": { backgroundColor: Colors.primaryDark } }}>
                {saving ? <CircularProgress size={14} sx={{ color: "#fff", mr: 1 }} /> : null}
                {saved ? "Guardado ✓" : "Guardar configuración"}
              </Button>
            </Box>
          </Box>
        </>
      )}
    </Paper>
  );
}

// ── Section edit drawer ────────────────────────────────────────────────────────

function SectionDrawer({ section, onClose, onSaved }) {
  const isNew   = !!section.isNew;
  const fileRef = useRef(null);

  const [displayName,  setDisplayName]  = useState(section.displayName  || "");
  const [subtitle,     setSubtitle]     = useState(section.subtitle     || "");
  const [firestoreTag, setFirestoreTag] = useState(section.firestoreTag || "");
  const [icon,         setIcon]         = useState(section.icon         || "");
  const [gradient,     setGradient]     = useState(section.gradient     || "");
  const [category,     setCategory]     = useState(section.category     || "trading");
  const [order,        setOrder]        = useState(section.order        ?? 0);
  const [imageURL,     setImageURL]     = useState(section.imageURL     || "");
  const [imageFile,    setImageFile]    = useState(null);
  const [imagePreview, setImagePreview] = useState(section.imageURL     || "");
  const [saving,       setSaving]       = useState(false);
  const [saveError,    setSaveError]    = useState("");
  const [uploadPct,    setUploadPct]    = useState(0);
  const [renameWarn,   setRenameWarn]   = useState(false);

  const tagChanged = !isNew && firestoreTag !== section.firestoreTag;

  function handleImagePick(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  }

  async function uploadImage(docId) {
    if (!imageFile) return imageURL;
    const ref  = storage.ref(`sections/${docId}/${imageFile.name}`);
    const task = ref.put(imageFile);
    await new Promise((res, rej) => {
      task.on("state_changed", s => setUploadPct(Math.round(s.bytesTransferred / s.totalBytes * 100)), rej, res);
    });
    return await ref.getDownloadURL();
  }

  async function batchRenameTag(oldTag, newTag) {
    const snap = await db.collection("academy_content").where("tags", "array-contains", oldTag).get();
    if (snap.empty) return;
    const BATCH_MAX = 500;
    let count = 0; let batch = db.batch();
    for (const doc of snap.docs) {
      batch.update(doc.ref, { tags: (doc.data().tags || []).map(t => t === oldTag ? newTag : t) });
      count++;
      if (count % BATCH_MAX === 0) { await batch.commit(); batch = db.batch(); }
    }
    if (count % BATCH_MAX !== 0) await batch.commit();
  }

  async function handleSave() {
    if (!displayName.trim()) { setSaveError("El nombre es requerido."); return; }
    if (!firestoreTag.trim()) { setSaveError("La etiqueta Firestore es requerida."); return; }
    if (tagChanged && !renameWarn) { setRenameWarn(true); return; }

    setSaving(true); setSaveError("");
    try {
      const docId      = section.id || displayName.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
      const uploadedURL = await uploadImage(docId);
      const data = {
        displayName: displayName.trim(), subtitle: subtitle.trim(),
        firestoreTag: firestoreTag.trim(), icon: icon.trim(),
        gradient: gradient.trim(), category, order: Number(order) || 0,
        imageURL: uploadedURL || imageURL || "",
      };
      await db.collection("sections").doc(docId).set(data, { merge: true });
      if (tagChanged) await batchRenameTag(section.firestoreTag, firestoreTag.trim());
      onSaved?.({ id: docId, ...data });
      onClose();
    } catch (err) { setSaveError(err.message || "Error al guardar."); }
    finally { setSaving(false); }
  }

  return (
    <Drawer anchor="right" open onClose={saving ? undefined : onClose}
      PaperProps={{ sx: { top: "72px", height: "calc(100% - 72px)" } }}>
      <Box sx={{ width: { xs: "100vw", sm: 460 }, p: 3, backgroundColor: Colors.bgSecondary, height: "100%", overflowY: "auto" }}>
        <Stack spacing={2.5}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h6" sx={{ color: Colors.textPrimary }}>{isNew ? "Nueva sección" : `Editar · ${section.displayName}`}</Typography>
            <IconButton size="small" onClick={onClose} sx={{ color: Colors.textMuted }}><MdClose /></IconButton>
          </Stack>

          {saveError && <Typography variant="body2" color="error">{saveError}</Typography>}

          {/* Image upload */}
          <Box sx={{ position: "relative", height: 160, borderRadius: 2, overflow: "hidden", background: imagePreview ? "none" : `linear-gradient(135deg, ${Colors.bgTertiary}, ${Colors.bgElevated})`, border: `2px dashed ${Colors.borderPrimary}`, cursor: "pointer" }}
            onClick={() => fileRef.current?.click()}>
            {imagePreview
              ? <img src={imagePreview} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              : <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", gap: 1 }}>
                  <MdImage size={36} color={Colors.textSecondary} />
                  <Typography variant="caption" sx={{ color: Colors.textSecondary }}>Click para subir imagen de portada</Typography>
                </Box>
            }
            {imagePreview && (
              <>
                <Box sx={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", opacity: 0, transition: "opacity 0.2s", "&:hover": { opacity: 1 } }}>
                  <Typography variant="caption" sx={{ color: "#fff", fontWeight: 600 }}>Cambiar imagen</Typography>
                </Box>
                <IconButton size="small" onClick={e => { e.stopPropagation(); setImagePreview(""); setImageFile(null); setImageURL(""); }}
                  sx={{ position: "absolute", top: 6, right: 6, background: "rgba(0,0,0,0.65)", color: "#fff" }}>
                  <MdClose size={14} />
                </IconButton>
              </>
            )}
          </Box>
          <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleImagePick} />
          {saving && uploadPct > 0 && uploadPct < 100 && <LinearProgress variant="determinate" value={uploadPct} sx={{ borderRadius: 4 }} />}

          <TextField label="Nombre visible" value={displayName} onChange={e => setDisplayName(e.target.value)} fullWidth placeholder="FOREX" />
          <TextField label="Subtítulo" value={subtitle} onChange={e => setSubtitle(e.target.value)} fullWidth placeholder="Descripción corta de la sección" />

          <Box>
            <TextField label="Etiqueta Firestore" value={firestoreTag}
              onChange={e => { setFirestoreTag(e.target.value); setRenameWarn(false); }}
              fullWidth placeholder="trading:forex" />
            {renameWarn && (
              <Paper sx={{ mt: 1, p: 2, background: `${Colors.warning}18`, border: `1px solid ${Colors.warning}60`, borderRadius: 2 }}>
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <MdWarning size={20} color={Colors.warning} style={{ flexShrink: 0, marginTop: 2 }} />
                  <Box>
                    <Typography variant="body2" sx={{ color: Colors.warning, fontWeight: 700 }}>¿Confirmar renombrado masivo?</Typography>
                    <Typography variant="caption" sx={{ color: Colors.textSecondary }}>
                      Actualizará <code>tags</code> en todos los cursos de <code style={{ color: Colors.warning }}>{section.firestoreTag}</code> → <code style={{ color: Colors.success }}>{firestoreTag}</code>.
                    </Typography>
                  </Box>
                </Stack>
                <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                  <Button size="small" variant="outlined" onClick={() => setRenameWarn(false)} sx={{ textTransform: "none" }}>Cancelar</Button>
                  <Button size="small" variant="contained" onClick={handleSave} sx={{ textTransform: "none", background: Colors.warning, "&:hover": { background: Colors.warningLight } }}>Confirmar y renombrar</Button>
                </Stack>
              </Paper>
            )}
          </Box>

          <TextField label="Icono (emoji)" value={icon} onChange={e => setIcon(e.target.value)} fullWidth placeholder="📈" />
          <TextField label="Gradiente CSS" value={gradient} onChange={e => setGradient(e.target.value)} fullWidth placeholder="linear-gradient(135deg, #1a0050, #6d28d9)" />
          <TextField select label="Categoría" value={category} onChange={e => setCategory(e.target.value)} fullWidth SelectProps={{ native: true }}>
            <option value="mastermind">NuMind (Mastermind)</option>
            <option value="trading">NuIncome (Trading)</option>
            <option value="nuwealth">NuWealth</option>
            <option value="nubusiness">NuBusiness</option>
          </TextField>
          <TextField label="Orden" type="number" value={order} onChange={e => setOrder(e.target.value)} fullWidth />

          <Divider sx={{ borderColor: Colors.borderSecondary }} />
          {!renameWarn && (
            <Stack direction="row" spacing={1.5}>
              <Button variant="outlined" onClick={onClose} disabled={saving} sx={{ textTransform: "none", borderRadius: 2 }}>Cancelar</Button>
              <Button variant="contained" onClick={handleSave} disabled={saving}
                sx={{ textTransform: "none", borderRadius: 2, background: Colors.primary, "&:hover": { background: Colors.primaryDark } }}>
                {saving ? "Guardando…" : "Guardar sección"}
              </Button>
            </Stack>
          )}
        </Stack>
      </Box>
    </Drawer>
  );
}

// ── Category promo panel ───────────────────────────────────────────────────────

const CATEGORY_LABELS_FULL = {
  mastermind: "NUMIND", trading: "NUINCOME", nuwealth: "NUWEALTH", nubusiness: "NUBUSINESS",
};

function CategoryPromoPanel({ cat }) {
  const promoFileRef = useRef(null);
  const [loaded,       setLoaded]       = useState(false);
  const [promoType,    setPromoType]    = useState("none");
  const [promoImageUrl,setPromoImageUrl]= useState("");
  const [promoVideoUrl,setPromoVideoUrl]= useState("");
  const [promoUploading,setPromoUploading]= useState(false);
  const [saving,       setSaving]       = useState(false);
  const [saved,        setSaved]        = useState(false);

  useEffect(() => {
    db.collection("academy_categories").doc(cat).get().then(snap => {
      if (snap.exists) {
        const d = snap.data();
        setPromoType(d.promoType || "none");
        setPromoImageUrl(d.promoImageUrl || "");
        setPromoVideoUrl(d.promoVideoUrl || "");
      }
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, [cat]);

  async function handleSave() {
    setSaving(true);
    try {
      await db.collection("academy_categories").doc(cat).set({ promoType, promoImageUrl: promoImageUrl.trim(), promoVideoUrl: promoVideoUrl.trim() });
      setSaved(true); setTimeout(() => setSaved(false), 2000);
    } catch (e) { alert("Error: " + e.message); }
    finally { setSaving(false); }
  }

  const tgSx = {
    "& .MuiToggleButton-root": { color: Colors.textSecondary, borderColor: Colors.borderSecondary, textTransform: "none", fontSize: 12, px: 1.5, gap: 0.5 },
    "& .Mui-selected": { color: `${Colors.primary} !important`, background: `${Colors.primary}22 !important`, borderColor: `${Colors.primary}66 !important` },
  };

  if (!loaded) return null;

  return (
    <Paper sx={{ mb: 1.5, p: 2.5, borderRadius: 2, border: `1px solid ${Colors.borderSecondary}`, background: Colors.bgSecondary }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Box>
          <Typography variant="subtitle2" sx={{ color: Colors.textPrimary, fontWeight: 700 }}>
            Promo · {CATEGORY_LABELS_FULL[cat]}
          </Typography>
          <Typography variant="caption" sx={{ color: Colors.textSecondary }}>
            Imagen o video visible en la página de categoría
          </Typography>
        </Box>
        <Button size="small" variant="contained" onClick={handleSave} disabled={saving}
          startIcon={saving ? <CircularProgress size={13} sx={{ color: "#fff" }} /> : <MdSave />}
          sx={{ textTransform: "none", borderRadius: 2, backgroundColor: saved ? Colors.success : Colors.primary, "&:hover": { backgroundColor: Colors.primaryDark } }}>
          {saved ? "Guardado ✓" : "Guardar"}
        </Button>
      </Stack>

      <ToggleButtonGroup value={promoType} exclusive onChange={(_, v) => v && setPromoType(v)} size="small" sx={{ ...tgSx, mb: 2 }}>
        <ToggleButton value="none">Sin promo</ToggleButton>
        <ToggleButton value="image"><MdImage size={13} style={{ marginRight: 4 }} />Imagen</ToggleButton>
        <ToggleButton value="video"><MdMovie size={13} style={{ marginRight: 4 }} />Video</ToggleButton>
      </ToggleButtonGroup>

      {promoType === "image" && (
        <>
          <Box onClick={() => !promoUploading && promoFileRef.current?.click()}
            sx={{
              height: 120, borderRadius: 2, overflow: "hidden", position: "relative", cursor: promoUploading ? "wait" : "pointer",
              background: promoImageUrl ? "none" : Colors.bgTertiary,
              border: `2px dashed ${promoImageUrl ? "transparent" : Colors.borderPrimary}`,
              "&:hover": { borderColor: promoImageUrl ? "transparent" : Colors.primary },
            }}>
            {promoImageUrl ? (
              <>
                <img src={promoImageUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                <Box sx={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", opacity: 0, transition: "opacity 0.2s", "&:hover": { opacity: 1 } }}>
                  <Typography variant="caption" sx={{ color: "#fff", fontWeight: 600 }}>Cambiar imagen</Typography>
                </Box>
                <IconButton size="small" onClick={e => { e.stopPropagation(); setPromoImageUrl(""); }}
                  sx={{ position: "absolute", top: 5, right: 5, background: "rgba(0,0,0,0.65)", color: "#fff", width: 22, height: 22, "&:hover": { background: "rgba(180,0,0,0.7)" } }}>
                  <MdClose size={13} />
                </IconButton>
              </>
            ) : promoUploading ? (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <Stack alignItems="center" justifyContent="center" sx={{ height: "100%", gap: 0.5 }}>
                <MdImage size={28} color={Colors.textMuted} />
                <Typography variant="caption" sx={{ color: Colors.textMuted }}>Subir imagen promocional</Typography>
              </Stack>
            )}
          </Box>
          <input ref={promoFileRef} type="file" accept="image/*" style={{ display: "none" }}
            onChange={async e => {
              const file = e.target.files?.[0];
              if (!file) return;
              e.target.value = "";
              setPromoUploading(true);
              try {
                const ref = storage.ref(`academy_categories/${cat}/promo_${Date.now()}_${file.name}`);
                await ref.put(file);
                setPromoImageUrl(await ref.getDownloadURL());
              } catch (err) { alert("Error subiendo imagen: " + err.message); }
              finally { setPromoUploading(false); }
            }}
          />
        </>
      )}

      {promoType === "video" && (
        <TextField size="small" label="URL del video"
          placeholder="https://... (YouTube, Vimeo, o MP4 directo)"
          value={promoVideoUrl} onChange={e => setPromoVideoUrl(e.target.value)}
          fullWidth
          InputProps={{ startAdornment: <InputAdornment position="start"><MdMovie color={Colors.textMuted} /></InputAdornment>, sx: { color: Colors.textPrimary, fontSize: 13 } }}
          sx={{ "& .MuiOutlinedInput-root": { background: Colors.bgTertiary, "& fieldset": { borderColor: Colors.borderSecondary } }, "& input": { color: Colors.textPrimary } }}
        />
      )}
    </Paper>
  );
}

// ── Main view ──────────────────────────────────────────────────────────────────

export default function AcademiaView() {
  const [sections,      setSections]      = useState([]);
  const [hero,          setHero]          = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [orderChanged,  setOrderChanged]  = useState(false);
  const [savingOrder,   setSavingOrder]   = useState(false);
  const [drawer,        setDrawer]        = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    Promise.all([fetchSections(), fetchHero()])
      .then(([secs, h]) => { setSections(secs); setHero(h); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  function handleDragEnd(event) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setSections(prev => {
      const oldIdx = prev.findIndex(s => s.id === active.id);
      const newIdx = prev.findIndex(s => s.id === over.id);
      return arrayMove(prev, oldIdx, newIdx);
    });
    setOrderChanged(true);
  }

  async function saveOrder() {
    setSavingOrder(true);
    try {
      const batch = db.batch();
      sections.forEach((sec, idx) => batch.update(db.collection("sections").doc(sec.id), { order: idx + 1 }));
      await batch.commit();
      setOrderChanged(false);
    } catch (e) { alert("Error guardando orden: " + e.message); }
    finally { setSavingOrder(false); }
  }

  function handleSectionSaved(updated) {
    setSections(prev => {
      const exists = prev.find(s => s.id === updated.id);
      return exists ? prev.map(s => s.id === updated.id ? { ...s, ...updated } : s) : [...prev, updated];
    });
  }

  // Group sections by category for display only
  const categories = ["mastermind", "trading", "nuwealth", "nubusiness"];
  const grouped = categories.map(cat => ({ cat, secs: sections.filter(s => s.category === cat) }));
  // Flat sorted list for DnD context
  const allIds = sections.map(s => s.id);

  return (
    <Box sx={{ width: "100%", py: 3 }}>
      <Container maxWidth="xl">

        {/* Page header */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
          <Box>
            <Typography variant="h5" sx={{ color: Colors.textPrimary, fontWeight: 700 }}>Gestión de Academia</Typography>
            <Typography variant="body2" sx={{ color: Colors.textSecondary, mt: 0.25 }}>
              Hero del ecosistema, secciones, orden y visualización — todo en un solo lugar
            </Typography>
          </Box>
          <Stack direction="row" spacing={1.5}>
            {orderChanged && (
              <Button variant="contained" onClick={saveOrder} disabled={savingOrder} startIcon={savingOrder ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <MdSave />}
                sx={{ textTransform: "none", borderRadius: 2, background: Colors.success, "&:hover": { background: Colors.successLight } }}>
                Guardar orden
              </Button>
            )}
            <Button variant="outlined" onClick={() => setDrawer({ isNew: true })} startIcon={<MdAdd />}
              sx={{ textTransform: "none", borderRadius: 2, borderColor: Colors.borderPrimary, color: Colors.textSecondary, "&:hover": { borderColor: Colors.primary, color: Colors.textPrimary } }}>
              Nueva sección
            </Button>
          </Stack>
        </Stack>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}><CircularProgress /></Box>
        ) : (
          <>
            {/* Hero panel */}
            {hero && <HeroPanel hero={hero} onChange={setHero} />}

            {/* Sections grouped by category — promo panel per group */}
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={allIds} strategy={verticalListSortingStrategy}>
                {grouped.map(({ cat, secs }) => (
                  <Box key={cat} mb={3}>
                    <Typography variant="overline" sx={{ color: Colors.textSecondary, letterSpacing: 2, display: "block", mb: 1 }}>
                      {CATEGORY_LABELS_FULL[cat] || cat.toUpperCase()}
                    </Typography>
                    <CategoryPromoPanel cat={cat} />
                    {secs.map(sec => (
                      <SortableSection
                        key={sec.id}
                        sec={sec}
                        onEdit={() => setDrawer(sec)}
                        onSaved={updated => setSections(prev => prev.map(s => s.id === updated.id ? { ...s, ...updated } : s))}
                      />
                    ))}
                  </Box>
                ))}
              </SortableContext>
            </DndContext>

            {sections.length === 0 && (
              <Paper sx={{ p: 5, textAlign: "center", border: `1px dashed ${Colors.borderSecondary}`, background: "transparent", borderRadius: 3 }}>
                <Typography sx={{ color: Colors.textMuted }}>No hay secciones aún.</Typography>
                <Button variant="outlined" sx={{ mt: 2, textTransform: "none" }} onClick={() => setDrawer({ isNew: true })}>+ Crear primera sección</Button>
              </Paper>
            )}
          </>
        )}

      </Container>

      {drawer !== null && (
        <SectionDrawer section={drawer} onClose={() => setDrawer(null)} onSaved={handleSectionSaved} />
      )}
    </Box>
  );
}
